import { LitElement, PropertyValueMap, css, html } from 'lit'
import { customElement, property, query } from 'lit/decorators.js'
import { ControlPosition, getFlexPositions } from './positions'
//import { storage } from '../../directives/localStorageDecorator'
//import { queryString } from '../../directives/queryStringDecorator'

import CHEVRON_SVG from '../../shared-assets/chevron_up.svg'
import ARROW_HOOK_SVG from '../../shared-assets/arrow_hook_left.svg'

type ScrollDirection = 'vertical' | 'horizontal'

const CHEVRON_UP = html`<img src=${CHEVRON_SVG} style="transform: rotate(0deg);" />`;
const CHEVRON_RIGHT = html`<img src=${CHEVRON_SVG} style="transform: rotate(90deg);" />`;
const CHEVRON_DOWN = html`<img src=${CHEVRON_SVG} style="transform: rotate(180deg);" />`;
const CHEVRON_LEFT = html`<img src=${CHEVRON_SVG} style="transform: rotate(270deg);" />`;
const ARROW_HORIZ_BACK = html`<img src=${ARROW_HOOK_SVG} style="transform: rotate(0deg) rotateY(0deg);" />`;
const ARROW_HORIZ_FORWARD = html`<img src=${ARROW_HOOK_SVG} style="transform: rotate(0deg) rotateY(180deg);" />`;
const ARROW_VERTI_BACK = html`<img src=${ARROW_HOOK_SVG} style="transform: rotate(90deg) rotateY(0deg);" />`;
const ARROW_VERTI_FORWARD = html`<img src=${ARROW_HOOK_SVG} style="transform: rotate(90deg) rotateY(180deg);" />`;

/**
 * A "chip" element
 */
@customElement('ep-sliding-pages')
export class EPSlidingPages extends LitElement {

  @property({ attribute: 'scroll-direction', reflect: true })
  scrollDirection: ScrollDirection = 'vertical'

  @property({ type: Boolean, attribute: 'inset', reflect: true })
  inset: boolean = false

  @property({ type: Boolean, attribute: 'wrap', reflect: true })
  pageWrapsAround: boolean = false

  // #region Page Number indicator
  
  @property({ attribute: 'page-number-position', reflect: true })
  pageNumberPosition?: ControlPosition = undefined

  get showPageNumber(): boolean {
    return this.pageNumberPosition !== undefined;
  }

  get pageNumberPositionX(): string {
    return getFlexPositions(this.pageNumberPosition)[1];
  }
  get pageNumberPositionY(): string {
    return getFlexPositions(this.pageNumberPosition)[0];
  }

  // #endregion

  //@storage({ key: 'my_page_number2' })
  //@queryString({ name: 'page' })
  @property({ type: Number, reflect: true })
  page: number = 0;

  @property()
  maxPage = 0

  get isOnFirstPage() {
    return this.page === 0;
  }

  get isOnLastPage() {
    return this.page >= (this.maxPage - 1);
  }

  @query('slot')
  slotElement?: HTMLSlotElement;

  render() {
    return html`
      <div class="wrap" style="${this.inset ? '' : '--inset-size: 0px;'}"
        data-scroll-direction="${this.scrollDirection}"
        data-inset="${this.inset}"
        data-show-page-number="${this.showPageNumber}"
        >
        <!-- Page number indicator -->
        <div id="ctrl_page_num" class="controls controls-pad-2" style="flex-direction: row; justify-content: ${this.pageNumberPositionX};">
          <!-- <div style="flex-grow: 1;"></div> -->
          <span class="pageControlChip chip-horizontal" style="align-self: ${this.pageNumberPositionY};" part="control-chip">
            ${this.page + 1} / ${this.maxPage}
          </span>
        </div>
        <!-- Page Forward/Backward -->
        <div id="ctrl_page_buttons" class="controls">
          <button
            @click=${() => this._handleNavigate(-1)}
            .disabled=${this.pageWrapsAround ? this.maxPage === 0 : this.isOnFirstPage}
            class="pageControlChip" part="control-chip"
            >
            ${
              this.pageWrapsAround && this.isOnFirstPage
              ? (
                this.scrollDirection === 'horizontal' ? ARROW_HORIZ_FORWARD : ARROW_VERTI_FORWARD
              )
              : (
                this.scrollDirection === 'horizontal' ? CHEVRON_LEFT : CHEVRON_UP
              )
            }
          </button>
          <!-- <span>${this.page}</span>
          |
          <span>${this.maxPage}</span> -->
          <div style="flex-grow: 1;"></div>
          <button
            @click=${() => this._handleNavigate(+1)}
            .disabled=${this.pageWrapsAround ? this.maxPage === 0 : this.isOnLastPage}
            class="pageControlChip" part="control-chip"
            >
            ${
              this.pageWrapsAround && this.isOnLastPage
              ? (
                this.scrollDirection === 'horizontal' ? ARROW_HORIZ_BACK : ARROW_VERTI_BACK
              )
              : (
                this.scrollDirection === 'horizontal' ? CHEVRON_RIGHT : CHEVRON_DOWN
              )
            }
          </button>
        </div>
        <slot @slotchange=${this._handleSlotChange}></slot>
      </div>
    `
  }

  protected _handleNavigate(delta: number) {
    //console.log(`before [ ${this.page} / ${this.maxPage} ]`, ' del ', delta)
    if (this.pageWrapsAround) {
      function mod(x: number, m: number) {
        return (x%m + m)%m;
      }
      this.page = mod(this.page + delta, this.maxPage);
    }
    else {
      this.page += delta;
    }
    //console.log(`after [ ${this.page} / ${this.maxPage} ]`, ' del ', delta)
  }

  protected _handleSlotChange() {
    let length = this.slotElement?.assignedElements().length;
    this.maxPage = length !== undefined ? length: 0;
  }

  protected updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    super.updated(_changedProperties)

    for(let [prop, _] of _changedProperties) {
      switch(prop) {
        // if page changed
        case 'page': {
          // adjust "scroll"
          this.slotElement?.assignedElements().forEach((element, index, array) => {
            // safety check
            if (element instanceof HTMLElement) {
              const translate = this.scrollDirection === 'horizontal' ? 'translateX' : 'translateY'
              element.style.transform = `${translate}(calc(-1 * ${this.page} * 100%))`;
            }
            //console.log('element', index, element)
          });
          break;
        }
      }
    }
  }

  static styles = css`
    :host {
      overflow: hidden;
      width: 100%;
      height: 100%;
      --chevron-size: 16px;
      --inset-size: var(--chevron-size);
      --color-text: rgba(40,40,40,0.5);
      --font-prose: ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans",sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji";
    }

    * {
      box-sizing: border-box;
    }

    .wrap {
      display: flex;
      flex-direction: column;
      flex-wrap: nowrap;
      overflow: hidden;
      width: 100%;
      height: 100%;
      position: relative;
    }

    .wrap[data-scroll-direction="horizontal"], .wrap[data-scroll-direction="horizontal"] > .controls {
      flex-direction: row;
      --inset-size-x: var(--inset-size);
      --inset-size-y: 0px;
    }
    .wrap[data-scroll-direction="vertical"], .wrap[data-scroll-direction="vertical"] > .controls {
      flex-direction: column;
      --inset-size-x: 0px;
      --inset-size-y: var(--inset-size);
    }

    .wrap[data-show-page-number="true"] {
      --inset-size-x: var(--inset-size);
      --inset-size-y: var(--inset-size);
    }

    .wrap[data-show-page-number="false"] #ctrl_page_num {
      display: none;
    }

    ::slotted(*) {
      /* display: flex; */
      flex: 1 0 100%;
      /* min-width: var(--calc-size-x);
      min-height: var(--calc-size-y); */
      overflow: auto;
      transition: transform 500ms;
      padding: var(--inset-size-y) var(--inset-size-x);
      box-sizing: border-box;
    }

    .controls {
      --padding: 1px;
      display: flex;
      pointer-events: none;
      /* width: calc(100% - 2 * var(--padding)); */
      width: 100%;
      /* height: calc(100% - 2 * var(--padding)); */
      height: 100%;
      position: absolute;
      /* background: rgba(255,255,255,0.5); */
      z-index: 99;
      padding: var(--padding);
    }
    .controls.controls-pad-2 {
      padding: calc(2 * var(--padding));
    }

    .controls button {
      pointer-events: auto;
      align-self: center;
      border: none;
    }

    .controls button img {
      display: block;
      width: var(--chevron-size);
      height: var(--chevron-size);
    }

    .pageControlChip {
      backdrop-filter: saturate(180%) blur(10px);
      padding: 0 12px;
      border-radius: 32px;
      cursor: pointer;
      background-color: hsla(0,0%,90%,.5);
      transition: transform 125ms, opacity 500ms;
    }

    button.pageControlChip:disabled {
      background-color: hsla(0,0%,90%,.5);
      cursor: initial;
      opacity: 0.25;
      pointer-events: none;
    }

    button.pageControlChip:not(:disabled):hover {
      transform: scale(1.05);
    }
    button.pageControlChip:not(:disabled):active {
      transform: scale(0.925);
    }

    .wrap[data-scroll-direction="horizontal"] .pageControlChip:not(.chip-horizontal) {
      padding: 12px 0;
    }

    .wrap[data-scroll-direction="vertical"] .pageControlChip:not(.chip-vertical) {
      padding: 0 12px;
    }

    .pageControlChip.chip-horizontal {
      padding: 0 12px;
      
    }
    .pageControlChip.chip-vertical {
      padding: 12px 0;
    }

    #ctrl_page_num {
      font-family: var(--font-prose);
      font-weight: 700;
      font-size: 0.8em;
      font-style: normal;
      line-height: 1.4;
      color: var(--color-text);
    }
  `
}

declare global {
  interface HTMLElementTagNameMap {
    'ep-sliding-pages': EPSlidingPages
  }
}
